var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { ColorEnum } from '../components/ColorPicker';
import { v4 as uuidv4 } from 'uuid';
import { DEFAULT_CHART_DATA } from '../helper/numberChartHelper';
import { makeDefaultRod } from '../widgets/NumberRackManipulative';
import { shuffleArray } from '../helper/shuffleArray';
var VALID_LINKING_CUBE_HEXES = new Set(Object.values(ColorEnum).map(function (h) { return h.toLowerCase(); }));
function resolveLinkingCubeColor(raw) {
    if (typeof raw === 'string') {
        // Accept ColorEnum keys like "BLACK", "DARK_NAVY_BLUE"
        var key = raw.toUpperCase();
        if (ColorEnum[key])
            return ColorEnum[key];
        // Accept hex if it's in the palette
        if (VALID_LINKING_CUBE_HEXES.has(raw.toLowerCase()))
            return raw;
    }
    return ColorEnum.BLACK;
}
// Maps save-state widget types to internal manipulative types
var WIDGET_TYPE_MAP = {
    roller: 'dice',
    dice_pool: 'dice',
    spinner: 'spinner',
    picker: 'deck_pool',
    // Server sends analog clocks as "analog" and timer clocks as "clock" — both
    // map to their WhiteBoard render-switch keys.
    analog: 'analog',
    clock: 'clock',
    // Some IDM servers send singular "numberChart" (no trailing 's') while
    // user-saved payloads use "numberCharts". Normalise both to the plural form
    // so case 'numberCharts' always handles them and data.id gets the correct
    // "numberChart_<uuid>" prefix (required for right-panel config key lookup).
    numberChart: 'numberCharts',
    // Same singular→plural normalisation for fractionTiles.
    fractionTile: 'fractionTiles',
    // Server may send "numberFrame" (singular) while internal type is "numberFrames".
    numberFrame: 'numberFrames',
};
// ─── Tray counter sizing ──────────────────────────────────────────────────
// TEMPORARILY DISABLED: common-scale logic that pinned the smallest counter
// to 60px wide and scaled the rest proportionally. Counters now render at
// their image's natural pixel size. Restore the block below when we revisit
// proportional sizing.
//
// const COUNTER_MIN_WIDTH_PX = 60;
// let counterCommonScale = 1;
var COUNTER_FALLBACK_PX = 60;
var counterImageSizeCache = new Map();
// Canonical tray-counter sizes from the BASE (untranslated / default-language)
// art, keyed by counterId. A counter's on-canvas size must NOT change with the
// UI language, but localized counter images can have different natural pixel
// dimensions (e.g. Spanish art is smaller). Sizing reads from here so a counter
// dragged in Spanish matches the English size — consistent with the
// language-switch re-merge, which preserves the originally-created size.
var counterBaseSizeCache = new Map();
/**
 * Preload the BASE (untranslated / default-language) tray-counter image sizes,
 * keyed by counterId. Always call with the untranslated trayCounters
 * (`rawInitial.trayCounters`) so the cached size is language-independent.
 */
export function preloadCounterBaseSizes(trayCounters) {
    console.log('[counter-size] preloadCounterBaseSizes called with', trayCounters.length, 'counters:', trayCounters.map(function (tc) { return ({ id: tc.counterId, front: tc.frontImage, primary: tc.primaryImage }); }));
    return Promise.all(trayCounters.map(function (tc) {
        return new Promise(function (resolve) {
            var url = tc.frontImage || tc.primaryImage;
            if (!url || counterBaseSizeCache.has(tc.counterId)) {
                console.log('[counter-size] base skip', tc.counterId, {
                    url: url,
                    alreadyCached: counterBaseSizeCache.has(tc.counterId),
                });
                return resolve();
            }
            var img = new Image();
            img.onload = function () {
                counterBaseSizeCache.set(tc.counterId, {
                    width: img.naturalWidth,
                    height: img.naturalHeight,
                });
                console.log('[counter-size] base CACHED', tc.counterId, {
                    url: url,
                    width: img.naturalWidth,
                    height: img.naturalHeight,
                });
                resolve();
            };
            img.onerror = function () {
                console.warn('[counter-size] base image FAILED to load', tc.counterId, url);
                resolve();
            };
            img.src = url;
        });
    })).then(function () { return undefined; });
}
/** Preload an arbitrary list of image URLs into `counterImageSizeCache`. */
export function preloadImageSizes(urls) {
    var unique = Array.from(new Set(urls.filter(Boolean)));
    return Promise.all(unique.map(function (url) {
        return new Promise(function (resolve) {
            if (counterImageSizeCache.has(url))
                return resolve();
            var img = new Image();
            img.onload = function () {
                counterImageSizeCache.set(url, {
                    width: img.naturalWidth,
                    height: img.naturalHeight,
                });
                resolve();
            };
            img.onerror = function () { return resolve(); };
            img.src = url;
        });
    })).then(function () { return undefined; });
}
export function preloadTrayCounterSizes(trayCounters) {
    var urls = Array.from(new Set(trayCounters
        .flatMap(function (tc) { return [tc.frontImage, tc.primaryImage, tc.backImage]; })
        .filter(function (u) { return !!u; })));
    return Promise.all(urls.map(function (url) {
        return new Promise(function (resolve) {
            if (counterImageSizeCache.has(url))
                return resolve();
            var img = new Image();
            img.onload = function () {
                counterImageSizeCache.set(url, {
                    width: img.naturalWidth,
                    height: img.naturalHeight,
                });
                resolve();
            };
            img.onerror = function () { return resolve(); };
            img.src = url;
        });
    })).then(function () {
        // Common-scale computation disabled — leaving images at natural size.
        // let minWidth = Infinity;
        // for (const tc of trayCounters) {
        //   const url = tc.frontImage || tc.primaryImage;
        //   const size = url ? counterImageSizeCache.get(url) : undefined;
        //   if (size && size.width > 0 && size.width < minWidth) minWidth = size.width;
        // }
        // counterCommonScale =
        //   minWidth > 0 && Number.isFinite(minWidth) ? COUNTER_MIN_WIDTH_PX / minWidth : 1;
    });
}
function resolveCounterDimensions(frontImage, counterId) {
    // Prefer the base-language size so a counter is sized identically in every
    // language — localized art (smaller naturals) must not shrink it.
    var base = counterId ? counterBaseSizeCache.get(counterId) : undefined;
    console.log('[counter-size] resolveCounterDimensions', {
        counterId: counterId,
        frontImage: frontImage,
        baseHit: !!base,
        base: base,
        localHit: !!(frontImage && counterImageSizeCache.get(frontImage)),
        local: frontImage ? counterImageSizeCache.get(frontImage) : undefined,
        baseCacheKeys: Array.from(counterBaseSizeCache.keys()),
    });
    if (base && base.width > 0 && base.height > 0)
        return { width: base.width, height: base.height };
    var size = frontImage ? counterImageSizeCache.get(frontImage) : undefined;
    if (!size)
        return { width: COUNTER_FALLBACK_PX, height: COUNTER_FALLBACK_PX };
    // Full-size: natural pixel dimensions. Previously this was
    // `size.width * counterCommonScale` for tray-wide proportional sizing.
    return { width: size.width, height: size.height };
}
/**
 * Extracts the UUID portion from a widgetId, stripping colons and dashes.
 * widgetId is either "type:uuid-with-dashes" or a plain string.
 */
function extractUuidPart(widgetId) {
    return widgetId.includes(':')
        ? widgetId.split(':')[1].replace(/-/g, '')
        : widgetId.replace(/-/g, '');
}
/**
 * Maps a save-state type string to internal manipulative type
 */
export function mapWidgetType(saveType) {
    var _a;
    return (_a = WIDGET_TYPE_MAP[saveType]) !== null && _a !== void 0 ? _a : saveType;
}
/**
 * Merges widget config from initial.json with position/state from save state.
 * Returns an array because group types (e.g. dice_pool) produce multiple manipulatives.
 */
export function mergeWidget(initialWidget, saveWidget, isShareCode, 
// Forwarded to deck merging: preserve the start-time deck size on a
// language-switch re-merge rather than recomputing it.
preserveDimensions, 
// Forwarded to deck merging: gates per-card image sizing + portrait decks.
isIdmOrSwp) {
    var _a, _b, _c, _d;
    if (isShareCode === void 0) { isShareCode = false; }
    if (preserveDimensions === void 0) { preserveDimensions = false; }
    if (isIdmOrSwp === void 0) { isIdmOrSwp = false; }
    var internalType = mapWidgetType(saveWidget.type);
    if (internalType === 'dice') {
        return mergeDicePool(initialWidget, saveWidget);
    }
    switch (internalType) {
        case 'spinner':
            return [mergeSpinnerWidget(initialWidget, saveWidget)];
        case 'deck_pool':
            return [
                mergeDeckWidget(initialWidget, saveWidget, isShareCode, preserveDimensions, isIdmOrSwp),
            ];
        case 'patternShapes':
            return [mergePatternShapesWidget(initialWidget, saveWidget)];
        case 'numberRacks':
            return [mergeNumberRacksWidget(initialWidget, saveWidget)];
        case 'numberFrames':
            return [mergeNumberFramesWidget(initialWidget, saveWidget)];
        case 'arrayModel':
            return [mergeArrayModelWidget(initialWidget, saveWidget)];
        case 'fractionTiles':
            return [mergeFractionTilesWidget(initialWidget, saveWidget)];
        case 'numberCharts':
            return [mergeNumberChartsWidget(initialWidget, saveWidget)];
        case 'numberPiece':
            return [mergeNumberPieceWidget(initialWidget, saveWidget)];
        case 'moneyPiece':
            return [mergeMoneyPieceWidget(initialWidget, saveWidget)];
        case 'coloredTiles':
            return [mergeColoredTilesWidget(initialWidget, saveWidget)];
        case 'linearPiece':
            return [mergeLinearPieceWidget(initialWidget, saveWidget)];
        case 'analog':
            return [mergeAnalogWidget(initialWidget, saveWidget)];
        case 'clock':
            return [mergeClockWidget(initialWidget, saveWidget)];
        default: {
            // Generic fallback — pass through save data with mapped type.
            var rawId = (_b = (_a = saveWidget.data) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : saveWidget.widgetId;
            // Downstream code (RightSideToolPanel config lookup via
            // `tool.split('_')[0]`, and `startsWith('<type>_')` checks) requires the
            // UNDERSCORE id form. A colon-delimited id (legacy/corrupt save, or the
            // widgetId fallback above) misses those matches, so e.g. the numberLine
            // right panel silently fails to open on reload. Normalize the type/uuid
            // separator from ':' to '_' on parse so the runtime uses the underscore
            // id — and the NEXT save persists it in the correct form.
            var resolvedId = rawId;
            if (typeof rawId === 'string' && rawId.includes(':')) {
                resolvedId = rawId.replace(':', '_');
                console.error('[WIDGET_MERGE] normalized colon id -> underscore', {
                    type: internalType,
                    widgetId: saveWidget.widgetId,
                    from: rawId,
                    to: resolvedId,
                    usedWidgetIdFallback: ((_c = saveWidget.data) === null || _c === void 0 ? void 0 : _c.id) == null,
                });
            }
            // jumpBars / customTicks reference their parent line via `numberLineId`
            // (= the line's data.id). Normalize it identically so the parent/child
            // link survives after the line's own id is rewritten to underscore form.
            var rawParentId = (_d = saveWidget.data) === null || _d === void 0 ? void 0 : _d.numberLineId;
            var normalizedData = typeof rawParentId === 'string' && rawParentId.includes(':')
                ? __assign(__assign({}, saveWidget.data), { numberLineId: rawParentId.replace(':', '_') }) : saveWidget.data;
            return [
                {
                    type: internalType,
                    widgetId: saveWidget.widgetId,
                    data: __assign(__assign({}, normalizedData), { id: resolvedId, type: internalType }),
                },
            ];
        }
    }
}
/**
 * Converts a raw face from initial.json to the internal diceFace format
 */
function convertFace(face) {
    var _a, _b;
    return {
        counterId: face.counterId,
        rotate: (_a = face.rotate) !== null && _a !== void 0 ? _a : false,
        frontImage: typeof face.frontImage === 'string'
            ? [{ type: 'image', value: face.frontImage }]
            : face.frontImage,
        primaryImage: typeof face.primaryImage === 'string'
            ? [{ type: 'image', value: face.primaryImage }]
            : face.primaryImage,
        style: (_b = face.style) !== null && _b !== void 0 ? _b : 'none',
    };
}
/**
 * Creates a single dice manipulativeInterface from initial dice config + save position
 */
function mergeSingleDice(initialWidget, saveWidget, diceIndex, groupId, groupCenter, 
// When true, the saveWidget already represents this individual die — its
// `data.center` is the die's actual position. Skip the diceIndex × 80 offset
// that's used during pool expansion (where one saveWidget seeds the whole
// group at a single anchor point).
useSavedCenter, 
// When provided (during pool expansion), lay dice out in a roughly square
// grid of `gridCols` columns instead of a single row.
gridCols) {
    var _a, _b, _c, _d, _e, _f, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10;
    if (useSavedCenter === void 0) { useSavedCenter = false; }
    var initialDice = (_b = (_a = initialWidget === null || initialWidget === void 0 ? void 0 : initialWidget.data) === null || _a === void 0 ? void 0 : _a.dice) !== null && _b !== void 0 ? _b : [];
    var diceConfig = initialDice[diceIndex];
    // Resolve the die label, preferring the first non-empty string among: the
    // per-die config label, the saved label, the pool-level config label, and the
    // pool-level saved label. Per-die labels are often `null`, with the real label
    // authored once at the pool level — fall through to it so each die still shows
    // the label individually.
    var diceLabel = (_j = [
        diceConfig === null || diceConfig === void 0 ? void 0 : diceConfig.label,
        (_c = saveWidget.data) === null || _c === void 0 ? void 0 : _c.label,
        (_d = initialWidget === null || initialWidget === void 0 ? void 0 : initialWidget.data) === null || _d === void 0 ? void 0 : _d.label,
        (_h = (_f = (_e = saveWidget.data) === null || _e === void 0 ? void 0 : _e.dice) === null || _f === void 0 ? void 0 : _f[diceIndex]) === null || _h === void 0 ? void 0 : _h.label,
    ].find(function (l) { return typeof l === 'string' && l.trim(); })) !== null && _j !== void 0 ? _j : '';
    // For non-IDM dice (no initialWidget), faces may be saved in the share-code
    // payload; prefer initialWidget's faces when present (IDM source of truth).
    var savedFaces = (_k = saveWidget.data) === null || _k === void 0 ? void 0 : _k.faces;
    var faces = (diceConfig === null || diceConfig === void 0 ? void 0 : diceConfig.faces)
        ? diceConfig.faces.map(convertFace)
        : Array.isArray(savedFaces) && savedFaces.length > 0
            ? savedFaces
            : [];
    // Determine diceType based on face content:
    //   'image' — faces have image URLs (renders <img> per face)
    //   'text'  — faces have text/math content (renders via MathEditor)
    //   'dot'   — no custom faces (renders from dot spritesheet)
    var hasImageFaces = faces.length > 0 && faces.some(function (f) { var _a; return (_a = f.frontImage) === null || _a === void 0 ? void 0 : _a.some(function (item) { return item.type === 'image'; }); });
    var hasTextFaces = !hasImageFaces &&
        faces.length > 0 &&
        faces.some(function (f) { var _a; return (_a = f.frontImage) === null || _a === void 0 ? void 0 : _a.some(function (item) { return item.type === 'text' && item.value.trim(); }); });
    // Prefer the saved diceType (correct for share-code round-trips) and fall
    // back to inference from face content.
    var diceType = (_m = (_l = saveWidget.data) === null || _l === void 0 ? void 0 : _l.diceType) !== null && _m !== void 0 ? _m : (hasImageFaces ? 'image' : hasTextFaces ? 'text' : 'dot');
    var diceId = "dice_".concat(uuidv4().replace(/-/g, ''));
    var center = (_p = (_o = saveWidget.data) === null || _o === void 0 ? void 0 : _o.center) !== null && _p !== void 0 ? _p : { x: 400, y: 400 };
    var defaultDimensions = { width: 96, height: 89 };
    var scale = (_r = (_q = saveWidget.data) === null || _q === void 0 ? void 0 : _q.scale) !== null && _r !== void 0 ? _r : 1;
    var dimensions = {
        width: defaultDimensions.width * scale,
        height: defaultDimensions.height * scale,
    };
    // Resolve the die's effective center:
    //   • useSavedCenter: this saveWidget IS the die — use its saved center as-is.
    //   • groupId + gridCols: pool expansion in a grid — offset by row/col.
    //   • groupId only: pool expansion in a single row (legacy fallback).
    //   • neither: ungrouped single die — use its saved center.
    var offsetCenter;
    if (useSavedCenter || !groupId) {
        offsetCenter = center;
    }
    else if (gridCols && gridCols > 0) {
        var col = diceIndex % gridCols;
        var row = Math.floor(diceIndex / gridCols);
        offsetCenter = { x: center.x + col * 80, y: center.y + row * 80 };
    }
    else {
        offsetCenter = { x: center.x + diceIndex * 80, y: center.y };
    }
    var diceDataObj = __assign(__assign(__assign(__assign(__assign(__assign({ id: diceId, type: 'dice', diceType: diceType, center: offsetCenter, dimensions: dimensions, scale: (_t = (_s = saveWidget.data) === null || _s === void 0 ? void 0 : _s.scale) !== null && _t !== void 0 ? _t : 1, activeFaceIndex: (_v = (_u = saveWidget.data) === null || _u === void 0 ? void 0 : _u.activeFaceIndex) !== null && _v !== void 0 ? _v : 0, faces: faces, label: diceLabel, color: (_x = (_w = saveWidget.data) === null || _w === void 0 ? void 0 : _w.color) !== null && _x !== void 0 ? _x : 'WHITE', defaultDimensions: defaultDimensions, 
        // IDM-author dice loaded from production JSON have no `lock` set, so the
        // fallback fires and pins them locked by default — students can unlock
        // explicitly. User-saved decks always include `lock` (see
        // `manipulativeToSaveWidget`), so reload preserves the chosen state.
        lock: (_z = (_y = saveWidget.data) === null || _y === void 0 ? void 0 : _y.lock) !== null && _z !== void 0 ? _z : { positioning: false, editing: false }, 
        // Derive from lock.editing rather than trusting the saved isPreset value, which may have
        // been corrupted by an earlier bad fix. IDM-authored dice always have editing:true (the
        // merge default); user-created dice always have editing:false (set in ManipulativePopup).
        // undefined (fresh IDM JSON, never saved) also maps to true via `!== false`.
        isPreset: (_1 = (_0 = saveWidget.data) === null || _0 === void 0 ? void 0 : _0.isPreset) !== null && _1 !== void 0 ? _1 : ((_4 = (_3 = (_2 = saveWidget.data) === null || _2 === void 0 ? void 0 : _2.lock) === null || _3 === void 0 ? void 0 : _3.editing) !== null && _4 !== void 0 ? _4 : true) !== false }, (groupId
        ? { groupId: groupId }
        : ((_5 = saveWidget.data) === null || _5 === void 0 ? void 0 : _5.groupId)
            ? { groupId: saveWidget.data.groupId }
            : {})), (groupCenter
        ? { groupCenter: groupCenter }
        : ((_6 = saveWidget.data) === null || _6 === void 0 ? void 0 : _6.groupCenter)
            ? { groupCenter: saveWidget.data.groupCenter }
            : {})), (groupId
        ? { groupPosition: diceIndex }
        : ((_7 = saveWidget.data) === null || _7 === void 0 ? void 0 : _7.groupPosition) !== undefined
            ? { groupPosition: saveWidget.data.groupPosition }
            : {})), (((_8 = saveWidget.data) === null || _8 === void 0 ? void 0 : _8.groupScale) !== undefined && { groupScale: saveWidget.data.groupScale })), (((_9 = saveWidget.data) === null || _9 === void 0 ? void 0 : _9.beforeGroupScale) !== undefined && {
        beforeGroupScale: saveWidget.data.beforeGroupScale,
    })), (typeof ((_10 = saveWidget.data) === null || _10 === void 0 ? void 0 : _10.vertical) === 'boolean'
        ? { vertical: saveWidget.data.vertical }
        : typeof (center === null || center === void 0 ? void 0 : center.vertical) === 'boolean'
            ? { vertical: center.vertical }
            : {}));
    return {
        type: 'dice',
        widgetId: saveWidget.widgetId,
        data: diceDataObj,
    };
}
/**
 * Merges a dice_pool widget: creates one dice manipulative per dice element in the pool.
 * Single-die pools produce an ungrouped die; multi-die pools share a groupId.
 */
function mergeDicePool(initialWidget, saveWidget) {
    var _a, _b, _c, _d, _e, _f, _h, _j, _k, _l;
    var initialDice = (_b = (_a = initialWidget === null || initialWidget === void 0 ? void 0 : initialWidget.data) === null || _a === void 0 ? void 0 : _a.dice) !== null && _b !== void 0 ? _b : [];
    var saveDice = Array.isArray((_c = saveWidget.data) === null || _c === void 0 ? void 0 : _c.dice) ? saveWidget.data.dice : [];
    // Prefer IDM initialDice for count; fall back to saveDice; default 1
    var diceCount = initialDice.length > 0 ? initialDice.length : saveDice.length > 0 ? saveDice.length : 1;
    if (diceCount <= 1) {
        var result = mergeSingleDice(initialWidget, saveWidget, 0);
        if ((_d = saveWidget.data) === null || _d === void 0 ? void 0 : _d.groupId) {
            // User-saved grouped die: preserve group fields and assign data.id as widgetId
            // so each die in the group gets a unique widgetId on subsequent saves, preventing
            // the deduplication from collapsing the group on future reloads.
            return [__assign(__assign({}, result), { widgetId: result.data.id })];
        }
        // Ungrouped single die — strip any stale group fields
        var _m = result.data, _g = _m.groupId, _gc = _m.groupCenter, _gp = _m.groupPosition, _gs = _m.groupScale, _bgs = _m.beforeGroupScale, cleanData = __rest(_m, ["groupId", "groupCenter", "groupPosition", "groupScale", "beforeGroupScale"]);
        return [__assign(__assign({}, result), { data: cleanData })];
    }
    // Multiple dice — generate a shared groupId and place each die in a grid
    // (cols × rows). The pool's alignment is driven by an optional `vertical`
    // flag on the authored `center` (production JSON):
    //   • vertical === true  → single column (dice stacked top-to-bottom)
    //   • vertical === false → single row    (dice laid left-to-right)
    //   • omitted            → roughly square grid inferred from the count
    //     (IDM dice_pool authoring carries no layout metadata by default).
    var groupId = "manipulativeGroup_".concat(uuidv4().replace(/-/g, ''));
    var center = (_f = (_e = saveWidget.data) === null || _e === void 0 ? void 0 : _e.center) !== null && _f !== void 0 ? _f : { x: 400, y: 400 };
    var scale = (_j = (_h = saveWidget.data) === null || _h === void 0 ? void 0 : _h.scale) !== null && _j !== void 0 ? _j : 1;
    var diceDims = { width: 96 * scale, height: 89 * scale };
    var verticalFlag = center === null || center === void 0 ? void 0 : center.vertical;
    var gridCols = verticalFlag === true
        ? 1
        : verticalFlag === false
            ? diceCount
            : Math.max(1, Math.ceil(Math.sqrt(diceCount)));
    var gridRows = Math.max(1, Math.ceil(diceCount / gridCols));
    // Prefer the saved groupCenter (share-code / user-state round-trips). Only
    // derive it from center when no saved position exists (IDM initial placement).
    var groupCenter = (_l = (_k = saveWidget.data) === null || _k === void 0 ? void 0 : _k.groupCenter) !== null && _l !== void 0 ? _l : (function () {
        var GAP = 20;
        var PADDING = 20;
        var totalWidth = gridCols * diceDims.width + (gridCols - 1) * GAP + 2 * PADDING;
        var totalHeight = gridRows * diceDims.height + (gridRows - 1) * GAP + 2 * PADDING;
        return { x: center.x - totalWidth / 2, y: center.y - totalHeight / 2 };
    })();
    var manipulatives = [];
    for (var i = 0; i < diceCount; i++) {
        manipulatives.push(mergeSingleDice(initialWidget, saveWidget, i, groupId, groupCenter, false, gridCols));
    }
    return manipulatives;
}
/**
 * Merges a dice pool from one or more saveWidget entries sharing a widgetId.
 *
 * Two shapes are supported:
 *   1. IDM placeholder: a single saveWidget with no per-die group metadata
 *      (no `groupId`/`groupPosition`). Expanded to `initialDice.length` dice
 *      via `mergeDicePool` and laid out in a grid.
 *   2. Per-die saves: one saveWidget per die in the pool, each with its own
 *      saved `center` and `groupPosition`. Produces exactly one die per entry,
 *      respecting each entry's saved center. This is what `manipulativeToSaveWidget`
 *      emits when the user saves an activity with a multi-die pool.
 */
export function mergeDicePoolEntries(initialWidget, entries) {
    var _a, _b, _c, _d, _e, _f, _h, _j, _k;
    if (entries.length === 0)
        return [];
    var first = entries[0];
    var hasGroupMeta = ((_a = first.data) === null || _a === void 0 ? void 0 : _a.groupId) !== undefined || ((_b = first.data) === null || _b === void 0 ? void 0 : _b.groupPosition) !== undefined;
    // Single placeholder with no per-die metadata → expand the IDM pool.
    if (entries.length === 1 && !hasGroupMeta) {
        return mergeDicePool(initialWidget, first);
    }
    // Multiple entries with no group metadata, where the initial config defines
    // at most a single die: these are separate single-die pools that happen to
    // share a widgetId (e.g. two independent dice placed from the same IDM
    // template). Expand each one independently — they are NOT a group, and each
    // must use the single die config (initialDice[0]) so both get their faces.
    // Indexing into initialDice by entry position would leave the 2nd+ dice with
    // no config (initialDice[1] is undefined) and therefore no images.
    var initialDiceCount = (_e = (_d = (_c = initialWidget === null || initialWidget === void 0 ? void 0 : initialWidget.data) === null || _c === void 0 ? void 0 : _c.dice) === null || _d === void 0 ? void 0 : _d.length) !== null && _e !== void 0 ? _e : 0;
    if (!hasGroupMeta && initialDiceCount <= 1) {
        return entries.flatMap(function (entry) { return mergeDicePool(initialWidget, entry); });
    }
    // Per-die saves: one saveWidget per die. Honour each entry's saved center
    // and `groupPosition` (face slot in the IDM template).
    var sharedGroupId = (_h = (_f = entries.find(function (e) { var _a; return (_a = e.data) === null || _a === void 0 ? void 0 : _a.groupId; })) === null || _f === void 0 ? void 0 : _f.data.groupId) !== null && _h !== void 0 ? _h : "manipulativeGroup_".concat(uuidv4().replace(/-/g, ''));
    var sharedGroupCenter = (_k = (_j = entries.find(function (e) { var _a; return (_a = e.data) === null || _a === void 0 ? void 0 : _a.groupCenter; })) === null || _j === void 0 ? void 0 : _j.data) === null || _k === void 0 ? void 0 : _k.groupCenter;
    return entries.map(function (entry, i) {
        var _a;
        var diceIndex = typeof ((_a = entry.data) === null || _a === void 0 ? void 0 : _a.groupPosition) === 'number' ? entry.data.groupPosition : i;
        return mergeSingleDice(initialWidget, entry, diceIndex, sharedGroupId, sharedGroupCenter, 
        /* useSavedCenter */ true);
    });
}
/**
 * Merges a deck/picker widget: brings in cards from initial.json, attaches counterId on each
 * card so entities can later be matched and rendered as placed cards.
 */
function mergeDeckWidget(initialWidget, saveWidget, isShareCode, 
// True on a language-switch re-merge: the deck size was already computed at
// initial load, so reuse the saved `dimensions` verbatim instead of
// re-deriving (which would inflate landscape decks on each switch).
preserveDimensions, 
// True for IDM or SWP activities (from the store flags). Gates the
// experimental per-card image sizing (`idmDeckHasCover` is unreliable because
// IDM/SWP decks can have no cover image) and forces the deck to portrait.
isIdmOrSwp) {
    var _a, _b, _c, _d, _e, _f, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11, _12, _13, _14, _15, _16, _17, _18, _19, _20, _21, _22, _23, _24, _25, _26, _27, _28, _29, _30, _31, _32, _33, _34, _35, _36, _37, _38, _39, _40, _41, _42, _43, _44, _45, _46, _47, _48, _49, _50, _51, _52, _53;
    if (isShareCode === void 0) { isShareCode = false; }
    if (preserveDimensions === void 0) { preserveDimensions = false; }
    if (isIdmOrSwp === void 0) { isIdmOrSwp = false; }
    // IDM payloads carry the card list inside `saveWidget.data.cards`; classic
    // (non-IDM) payloads carry it in `initialWidget.data.cards`. Fall back so
    // both shapes render.
    var initialCards = (_a = initialWidget === null || initialWidget === void 0 ? void 0 : initialWidget.data) === null || _a === void 0 ? void 0 : _a.cards;
    var saveCards = (_b = saveWidget.data) === null || _b === void 0 ? void 0 : _b.cards;
    // When BOTH sides have a cards array, merge user state (placedOnDeck,
    // position, showImage, lock, zIndex, id) from saveCards into initialCards
    // — matched by counterId, falling back to id. Without this, the user's
    // dealt-card state evaporates whenever mergeDeckWidget rebuilds the deck
    // from initial config (notably during the language relocalize re-merge,
    // where the live deck is re-merged against the translated initialCards).
    //
    // Per-counterId queues: IDM decks can have multiple copies of the same
    // counterId (e.g. 4 copies of "counter:5031"). A flat Map<counterId, card>
    // only stores the LAST save card for each counterId, so overlayUserState
    // would assign that same state (and ID) to ALL initial copies — duplicating
    // IDs and producing wrong placedOnDeck counts. Queues consume one save card
    // per initial card in order, correctly distributing individual dealt states.
    var saveCardQueues = new Map();
    var saveCardsById = new Map();
    if (Array.isArray(saveCards)) {
        for (var _i = 0, saveCards_1 = saveCards; _i < saveCards_1.length; _i++) {
            var sc = saveCards_1[_i];
            if (sc === null || sc === void 0 ? void 0 : sc.counterId) {
                if (!saveCardQueues.has(sc.counterId))
                    saveCardQueues.set(sc.counterId, []);
                saveCardQueues.get(sc.counterId).push(sc);
            }
            if (sc === null || sc === void 0 ? void 0 : sc.id)
                saveCardsById.set(sc.id, sc);
        }
    }
    var overlayUserState = function (base) {
        var _a, _b, _c, _d, _e, _f, _h, _j, _k, _l, _m, _o;
        // Exact id match first (stable across re-merges when the save carries ids)
        var byId = saveCardsById.get(base === null || base === void 0 ? void 0 : base.id);
        if (byId) {
            return __assign(__assign(__assign({}, base), { placedOnDeck: (_a = byId.placedOnDeck) !== null && _a !== void 0 ? _a : base.placedOnDeck, position: (_b = byId.position) !== null && _b !== void 0 ? _b : base.position, showImage: (_c = byId.showImage) !== null && _c !== void 0 ? _c : base.showImage, lock: (_d = byId.lock) !== null && _d !== void 0 ? _d : base.lock, zIndex: (_e = byId.zIndex) !== null && _e !== void 0 ? _e : base.zIndex, id: (_f = byId.id) !== null && _f !== void 0 ? _f : base.id }), (byId.drawCount !== undefined && { drawCount: byId.drawCount }));
        }
        // Consume one save card from this counterId's queue so duplicate cards
        // (multiple initial cards sharing the same counterId) each get their own
        // individual saved state rather than all getting the same last-seen entry.
        var queue = (base === null || base === void 0 ? void 0 : base.counterId) ? saveCardQueues.get(base.counterId) : undefined;
        var u = (queue === null || queue === void 0 ? void 0 : queue.length) ? queue.shift() : undefined;
        if (!u)
            return base;
        return __assign(__assign(__assign(__assign(__assign({}, base), { placedOnDeck: (_h = u.placedOnDeck) !== null && _h !== void 0 ? _h : base.placedOnDeck, position: (_j = u.position) !== null && _j !== void 0 ? _j : base.position, showImage: (_k = u.showImage) !== null && _k !== void 0 ? _k : base.showImage, lock: (_l = u.lock) !== null && _l !== void 0 ? _l : base.lock, zIndex: (_m = u.zIndex) !== null && _m !== void 0 ? _m : base.zIndex, 
            // Preserve runtime card id so listeners/undo-redo tracking by id stay
            // stable across re-merges.
            id: (_o = u.id) !== null && _o !== void 0 ? _o : base.id }), (u.dimensions && { dimensions: u.dimensions })), (typeof u.rotate === 'boolean' && { rotate: u.rotate })), (u.drawCount !== undefined && { drawCount: u.drawCount }));
    };
    // infiniteCard decks grow their `cards` array at runtime (each empty-deck
    // click appends a whole new cloned batch) — the authored `initialCards` is
    // a fixed-size template and reconciling against it via overlayUserState
    // would silently drop every appended card beyond initialCards.length. Once
    // a save exists, trust it as the full, already-evolved source of truth
    // instead of re-deriving from the static template.
    var isInfiniteCard = Boolean((_d = (_c = saveWidget.data) === null || _c === void 0 ? void 0 : _c.infiniteCard) !== null && _d !== void 0 ? _d : (_e = initialWidget === null || initialWidget === void 0 ? void 0 : initialWidget.data) === null || _e === void 0 ? void 0 : _e.infiniteCard);
    var unswappedCards = isInfiniteCard && Array.isArray(saveCards) && saveCards.length > 0
        ? saveCards
        : Array.isArray(initialCards) && initialCards.length > 0
            ? initialCards.map(overlayUserState)
            : Array.isArray(saveCards)
                ? saveCards
                : [];
    // When the deck is authored with pathInterchange, each card's front/back
    // (and primary/secondary) image roles are swapped — what would normally
    // show on the front shows on the back and vice versa.
    var pathInterchange = Boolean((_h = (_f = saveWidget.data) === null || _f === void 0 ? void 0 : _f.pathInterchange) !== null && _h !== void 0 ? _h : (_j = initialWidget === null || initialWidget === void 0 ? void 0 : initialWidget.data) === null || _j === void 0 ? void 0 : _j.pathInterchange);
    var rawCards = pathInterchange
        ? unswappedCards.map(function (raw) { return (__assign(__assign({}, raw), { frontImage: raw.backImage, backImage: raw.frontImage, primaryImage: raw.secondaryImage, secondaryImage: raw.primaryImage })); })
        : unswappedCards;
    var rawDeckCenter = (_l = (_k = saveWidget.data) === null || _k === void 0 ? void 0 : _k.center) !== null && _l !== void 0 ? _l : { x: 400, y: 400 };
    // Our canonical card proportions (portrait). Height is always derived from
    // width via this ratio so cards keep OUR aspect ratio regardless of the
    // (height especially) values production sends.
    var defaultDeckDimensions = { width: 215, height: 301 };
    var CARD_ASPECT_RATIO = defaultDeckDimensions.width / defaultDeckDimensions.height; // ≈ 0.714
    // Share-code JSON round-trips our own saved dimensions verbatim. For production
    // JSON, production's height is unreliable, so take only THEIR width (deck, then
    // first card) and recompute the height from our aspect ratio. Falls back to the
    // canonical default when production gives us no usable width.
    var theirWidth = (_x = (_u = (_p = (_o = (_m = saveWidget.data) === null || _m === void 0 ? void 0 : _m.dimensions) === null || _o === void 0 ? void 0 : _o.width) !== null && _p !== void 0 ? _p : (_t = (_s = (_r = (_q = saveWidget.data) === null || _q === void 0 ? void 0 : _q.cards) === null || _r === void 0 ? void 0 : _r[0]) === null || _s === void 0 ? void 0 : _s.dimensions) === null || _t === void 0 ? void 0 : _t.width) !== null && _u !== void 0 ? _u : (_w = (_v = initialWidget === null || initialWidget === void 0 ? void 0 : initialWidget.data) === null || _v === void 0 ? void 0 : _v.dimensions) === null || _w === void 0 ? void 0 : _w.width) !== null && _x !== void 0 ? _x : (_1 = (_0 = (_z = (_y = initialWidget === null || initialWidget === void 0 ? void 0 : initialWidget.data) === null || _y === void 0 ? void 0 : _y.cards) === null || _z === void 0 ? void 0 : _z[0]) === null || _0 === void 0 ? void 0 : _0.dimensions) === null || _1 === void 0 ? void 0 : _1.width;
    // Orientation inference for IDM: take the deck_image (card back) or the
    // first card's image and compare its preloaded natural size. Landscape →
    // rotate=true. Non-IDM decks still honour `initialWidget.data.rotate`.
    // Computed BEFORE the dimensions so the size is derived in its final
    // orientation directly.
    var deckBackUrl = (_4 = (_3 = (_2 = saveWidget.data) === null || _2 === void 0 ? void 0 : _2.deck_image) === null || _3 === void 0 ? void 0 : _3.frontImage) !== null && _4 !== void 0 ? _4 : (_6 = (_5 = saveWidget.data) === null || _5 === void 0 ? void 0 : _5.deck_image) === null || _6 === void 0 ? void 0 : _6.primaryImage;
    var deckImageSrc = (_7 = (typeof deckBackUrl === 'string' ? deckBackUrl : undefined)) !== null && _7 !== void 0 ? _7 : (((_8 = rawCards[0]) === null || _8 === void 0 ? void 0 : _8.frontImage) || ((_9 = rawCards[0]) === null || _9 === void 0 ? void 0 : _9.primaryImage) || '');
    var inferredSize = typeof deckImageSrc === 'string' ? counterImageSizeCache.get(deckImageSrc) : undefined;
    var autoRotate = inferredSize && inferredSize.width > inferredSize.height ? true : undefined;
    // IDM/SWP decks are always shown portrait (rotate=false), regardless of saved
    // / inferred orientation. Non-IDM/SWP decks keep their normal orientation.
    var rotate = ((_10 = saveWidget.data) === null || _10 === void 0 ? void 0 : _10.rotate) !== undefined
        ? saveWidget.data.rotate
        : isIdmOrSwp
            ? false
            : ((_13 = (_12 = (_11 = initialWidget === null || initialWidget === void 0 ? void 0 : initialWidget.data) === null || _11 === void 0 ? void 0 : _11.rotate) !== null && _12 !== void 0 ? _12 : autoRotate) !== null && _13 !== void 0 ? _13 : false);
    // Deck-authored flag (IDM JSON, or non-IDM initial.json): when true, cards
    // deal face-down with this orientation instead of the normal face-up/rotate
    // default. Save state takes priority so a user's in-progress save wins on
    // re-merge, same precedence as `rotate` above.
    var initialRotate = (_15 = (_14 = saveWidget.data) === null || _14 === void 0 ? void 0 : _14.initialRotate) !== null && _15 !== void 0 ? _15 : (_16 = initialWidget === null || initialWidget === void 0 ? void 0 : initialWidget.data) === null || _16 === void 0 ? void 0 : _16.initialRotate;
    // Build deck/card dimensions from a portrait (narrow) width, keeping OUR card
    // aspect ratio and orienting by `rotate` (landscape → wide × tall).
    var orientedDims = function (portraitWidth) {
        return rotate
            ? { width: Math.round(portraitWidth / CARD_ASPECT_RATIO), height: portraitWidth }
            : { width: portraitWidth, height: Math.round(portraitWidth / CARD_ASPECT_RATIO) };
    };
    // The deck size is computed ONCE, at the initial load, in its FINAL
    // orientation — there is no "saved dimensions are always portrait, swap if
    // landscape" step. Share-code JSON and the language-switch re-merge
    // (`preserveDimensions`) both carry dimensions that are already final, so
    // they're reused verbatim — keeping the deck the same size across switches
    // regardless of orientation. Otherwise (production load) production's height
    // is unreliable: derive the height from THEIR width via our card aspect
    // ratio, orienting the result by `rotate` (landscape → wide × tall).
    var dimensions = isShareCode || preserveDimensions
        ? ((_18 = (_17 = saveWidget.data) === null || _17 === void 0 ? void 0 : _17.dimensions) !== null && _18 !== void 0 ? _18 : defaultDeckDimensions)
        : typeof theirWidth === 'number' && theirWidth > 0
            ? orientedDims(theirWidth)
            : rotate
                ? { width: defaultDeckDimensions.height, height: defaultDeckDimensions.width }
                : defaultDeckDimensions;
    // User-saved cards carry deckId in the correct "deck_<uuid>" format inside
    // each card. Prefer that over widgetId ("cards:1") which has no underscore and
    // breaks split('_')[0] → rightConfig / toolbox lookups.
    // Always search saveCards first (they have the uuid), even when rawCards came
    // from initialCards (IDM path where initialCards lack deckId entirely).
    var savedCardDeckId = Array.isArray(saveCards)
        ? (_19 = saveCards[0]) === null || _19 === void 0 ? void 0 : _19.deckId
        : undefined;
    var firstCardDeckId = savedCardDeckId !== null && savedCardDeckId !== void 0 ? savedCardDeckId : (_20 = rawCards[0]) === null || _20 === void 0 ? void 0 : _20.deckId;
    var uuidPartDeck = extractUuidPart(saveWidget.widgetId);
    var savedDataId = (_21 = saveWidget.data) === null || _21 === void 0 ? void 0 : _21.id;
    var deckId = typeof savedDataId === 'string' && savedDataId.startsWith('deck_')
        ? savedDataId // persisted by manipulativeToSaveWidget — authoritative runtime id
        : typeof firstCardDeckId === 'string' && firstCardDeckId.startsWith('deck_')
            ? firstCardDeckId
            : "deck_".concat(uuidPartDeck);
    // Production deck centers are now passed through unchanged — the PDF-inset
    // shift in mergePageData already lands the deck on the page background, so
    // the legacy −w/2, +h/2 anchor correction is no longer needed for decks.
    // Share-code data was already passing through unchanged.
    var center = rawDeckCenter;
    var allowDuplicateCards = (_23 = (_22 = initialWidget === null || initialWidget === void 0 ? void 0 : initialWidget.data) === null || _22 === void 0 ? void 0 : _22.allowDuplicateCards) !== null && _23 !== void 0 ? _23 : true;
    var filteredCards = allowDuplicateCards
        ? rawCards
        : rawCards.filter(function (raw, i, arr) { return arr.findIndex(function (r) { return r.counterId === raw.counterId; }) === i; });
    // IDM swaps the face roles: the deck's shared cover renders on the front
    // (everyone sees the same deck artwork before flipping), and each card's
    // unique `frontImage` renders on the back so flipping reveals the face.
    // Non-IDM keeps the classic mapping: per-card frontImage on the front,
    // deck cover on the back.
    var idmDeckHasCover = typeof deckBackUrl === 'string' && !!deckBackUrl;
    // `deckSizeOnly` (production JSON): authored on the deck's `dimensions`. When
    // true, the deck pile keeps the deck `dimensions` but each drawn card is sized
    // to fit its OWN card image (the image's natural size + aspect ratio) rather
    // than our fixed card aspect ratio. Also read from a persisted top-level flag
    // so it round-trips through share-code / re-save.
    var deckSizeOnly = (_33 = (_30 = (_28 = (_25 = (_24 = saveWidget.data) === null || _24 === void 0 ? void 0 : _24.deckSizeOnly) !== null && _25 !== void 0 ? _25 : (_27 = (_26 = saveWidget.data) === null || _26 === void 0 ? void 0 : _26.dimensions) === null || _27 === void 0 ? void 0 : _27.deckSizeOnly) !== null && _28 !== void 0 ? _28 : (_29 = initialWidget === null || initialWidget === void 0 ? void 0 : initialWidget.data) === null || _29 === void 0 ? void 0 : _29.deckSizeOnly) !== null && _30 !== void 0 ? _30 : (_32 = (_31 = initialWidget === null || initialWidget === void 0 ? void 0 : initialWidget.data) === null || _31 === void 0 ? void 0 : _31.dimensions) === null || _32 === void 0 ? void 0 : _32.deckSizeOnly) !== null && _33 !== void 0 ? _33 : false;
    var cards = filteredCards.map(function (raw, idx) {
        var _a, _b, _c, _d, _e, _f, _h, _j, _k, _l, _m, _o;
        var frontImageUrl = typeof raw.frontImage === 'string'
            ? raw.frontImage
            : ((_d = (_c = (_b = (_a = raw.frontImage) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.value) !== null && _c !== void 0 ? _c : raw.primaryImage) !== null && _d !== void 0 ? _d : '');
        var cardText = idmDeckHasCover ? deckBackUrl : frontImageUrl;
        var perCardBack = idmDeckHasCover ? frontImageUrl : undefined;
        var rawId = raw.id;
        var cardId = typeof rawId === 'string' && rawId.startsWith('card_')
            ? rawId
            : "card_".concat((_e = raw.counterId) !== null && _e !== void 0 ? _e : uuidv4().replace(/-/g, ''), "_").concat(idx);
        // ─── EXPERIMENTAL · IDM/SWP dealt-card size from card image (easy to remove)
        // For IDM/SWP cards only: a drawn card takes its orientation and DOMINANT
        // dimension from its own card image, then derives the other dimension from
        // OUR card aspect ratio (so cards keep our proportions, not the image's):
        //   • landscape image (wider than tall) → width from image,
        //     height = width × CARD_ASPECT_RATIO
        //   • portrait image (taller than wide)  → height from image,
        //     width = height × CARD_ASPECT_RATIO
        // Independent of the deck (which is forced portrait above). The image
        // element (`.cardImage`/`.backCardImg`) is inset by 5% of each axis
        // (width/height 90%, offset 5%); scaling the dominant dimension up by 1/0.9
        // lands the inset card at the image's natural dominant size. The deck stack
        // keeps the deck `dimensions`. Natural sizes are preloaded into
        // `counterImageSizeCache` during buildPagesData. To revert: delete this
        // block, change `dimensions: cardDimensions` back to `dimensions`, and drop
        // `rotate: cardRotate` below.
        var CARD_IMG_PAD = 0.05; // must match the 5% inset on .cardImage/.backCardImg
        // On a language-switch re-merge (`preserveDimensions`) OR a share-code
        // reload (`isShareCode`) the card was already sized at the initial load.
        // Reuse the live/saved card's size + orientation verbatim so switching
        // language, or simply reloading a save, never resizes cards — recomputing
        // below without a matching `cardImgSize` (non-IDM/SWP, non-deckSizeOnly
        // decks never populate it) would otherwise fall through to the full deck
        // `dimensions`, inflating every card to the deck's size.
        var preservedCardDims = (preserveDimensions || isShareCode) &&
            raw.dimensions &&
            raw.dimensions.width > 0 &&
            raw.dimensions.height > 0
            ? raw.dimensions
            : undefined;
        var cardImgSize = (isIdmOrSwp || deckSizeOnly) && !preservedCardDims
            ? counterImageSizeCache.get(frontImageUrl)
            : undefined;
        var padScale = 1 / (1 - 2 * CARD_IMG_PAD); // outer = content / 0.9
        // Per-card orientation from the image. Landscape only when the width
        // exceeds the height by more than a small tolerance — near-square images
        // (e.g. 350×349) read as portrait, not landscape. Tolerance is a ratio so
        // it scales with image size (2% ≈ the "within a few px" rule). Falls back
        // to the deck `rotate` for non-IDM/SWP (no cardImgSize). When reusing a
        // preserved size, keep the live card's saved orientation.
        var LANDSCAPE_TOLERANCE = 0.02;
        var cardRotate = preservedCardDims
            ? typeof raw.rotate === 'boolean'
                ? raw.rotate
                : rotate
            : cardImgSize
                ? cardImgSize.width > cardImgSize.height * (1 + LANDSCAPE_TOLERANCE)
                : rotate;
        var cardDimensions = preservedCardDims !== null && preservedCardDims !== void 0 ? preservedCardDims : dimensions;
        if (!preservedCardDims && cardImgSize && cardImgSize.width > 0 && cardImgSize.height > 0) {
            if (deckSizeOnly) {
                // deckSizeOnly: the drawn card fits its OWN image — BOTH dimensions come
                // from the image, keeping the image's aspect ratio (the card-back aspect
                // ratio is intentionally NOT applied). The 90%-inset content then lands
                // at the image's natural size. The deck pile keeps the deck `dimensions`.
                cardDimensions = {
                    width: Math.round(cardImgSize.width * padScale),
                    height: Math.round(cardImgSize.height * padScale),
                };
            }
            else if (cardRotate) {
                // Landscape: width from image, height from our aspect ratio.
                var width = Math.round(cardImgSize.width * padScale);
                cardDimensions = { width: width, height: Math.round(width * CARD_ASPECT_RATIO) };
            }
            else {
                // Portrait: height from image, width from our aspect ratio.
                var height = Math.round(cardImgSize.height * padScale);
                cardDimensions = { width: Math.round(height * CARD_ASPECT_RATIO), height: height };
            }
        }
        // ──────────────────────────────────────────────────────────────────────────
        var card = __assign(__assign(__assign({ id: cardId, counterId: (_f = raw.counterId) !== null && _f !== void 0 ? _f : cardId, probabilityId: (_h = raw.counterId) !== null && _h !== void 0 ? _h : cardId, type: (_j = raw.type) !== null && _j !== void 0 ? _j : 'image', text: raw.text !== undefined ? raw.text : cardText, zIndex: idx, dimensions: cardDimensions, rotate: cardRotate, placedOnDeck: (_k = raw.placedOnDeck) !== null && _k !== void 0 ? _k : false, position: (_l = raw.position) !== null && _l !== void 0 ? _l : { x: 0, y: 0 }, showImage: (_m = raw.showImage) !== null && _m !== void 0 ? _m : 'front', deckId: deckId, lock: (_o = raw.lock) !== null && _o !== void 0 ? _o : { positioning: false, editing: false } }, (raw.drawCount !== undefined && { drawCount: raw.drawCount })), (perCardBack && { backImage: perCardBack })), (raw.backImage && !perCardBack && !isIdmOrSwp && { backImage: raw.backImage }));
        return card;
    });
    // Shuffle unplaced cards on initial load so each session starts with a
    // randomised deck order. Placed cards keep their positions.
    var shuffledCards = __spreadArray(__spreadArray([], shuffleArray(cards.filter(function (c) { return !c.placedOnDeck; })), true), cards.filter(function (c) { return c.placedOnDeck; }), true);
    // Prefer the INITIAL config label (it carries the localized text after a
    // language switch, where `initialWidget` is the translated widget). The saved
    // label still holds the previous language's text, so it's only a fallback for
    // when the translation/initial has no label. The authored deck label isn't
    // user-editable, so initial-first is safe.
    var deckLabel = typeof ((_34 = initialWidget === null || initialWidget === void 0 ? void 0 : initialWidget.data) === null || _34 === void 0 ? void 0 : _34.label) === 'string' && initialWidget.data.label.trim()
        ? initialWidget.data.label
        : typeof ((_35 = saveWidget.data) === null || _35 === void 0 ? void 0 : _35.label) === 'string' && saveWidget.data.label.trim()
            ? saveWidget.data.label
            : undefined;
    var deckDataObj = __assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign({ id: deckId, type: 'deck', cards: shuffledCards, rotate: rotate }, (initialRotate !== undefined && { initialRotate: initialRotate })), (!isIdmOrSwp &&
        typeof deckBackUrl === 'string' &&
        deckBackUrl && { backImage: deckBackUrl })), (deckLabel && { label: deckLabel })), (deckSizeOnly && { deckSizeOnly: true })), (pathInterchange && { pathInterchange: pathInterchange })), { 
        // Same persistence rationale as pathInterchange above — IDM decks have no
        // matching initial.json pageWidget once the user's own save becomes
        // authoritative, so this is carried on the runtime deck data too. Always
        // written explicitly (defaults to false) rather than conditionally
        // spread, so decks are non-infinite unless the author opts in.
        infiniteCard: isInfiniteCard, deckType: (_37 = (_36 = saveWidget.data) === null || _36 === void 0 ? void 0 : _36.deckType) !== null && _37 !== void 0 ? _37 : 'preMadeDeck', deckIdx: (_39 = (_38 = saveWidget.data) === null || _38 === void 0 ? void 0 : _38.deckIdx) !== null && _39 !== void 0 ? _39 : 0, gradeIdx: (_41 = (_40 = saveWidget.data) === null || _40 === void 0 ? void 0 : _40.gradeIdx) !== null && _41 !== void 0 ? _41 : 0, center: center, dimensions: dimensions, scale: (_43 = (_42 = saveWidget.data) === null || _42 === void 0 ? void 0 : _42.scale) !== null && _43 !== void 0 ? _43 : 1, 
        // Default deck-pool widgets to locked when the source JSON has no lock
        // set (IDM authoring path). User saves carry their own `lock` so the
        // chosen state round-trips through the `??` fallback.
        lock: (_45 = (_44 = saveWidget.data) === null || _44 === void 0 ? void 0 : _44.lock) !== null && _45 !== void 0 ? _45 : { positioning: false, editing: false }, 
        // Use presence of saved `lock` as the user-created signal (same logic as dice/spinner merge).
        isPreset: (_47 = (_46 = saveWidget.data) === null || _46 === void 0 ? void 0 : _46.isPreset) !== null && _47 !== void 0 ? _47 : ((_48 = saveWidget.data) === null || _48 === void 0 ? void 0 : _48.lock) === undefined }), (((_49 = saveWidget.data) === null || _49 === void 0 ? void 0 : _49.defaultDimensions) && {
        defaultDimensions: saveWidget.data.defaultDimensions,
    })), (((_50 = saveWidget.data) === null || _50 === void 0 ? void 0 : _50.groupId) && { groupId: saveWidget.data.groupId })), (((_51 = saveWidget.data) === null || _51 === void 0 ? void 0 : _51.groupCenter) && { groupCenter: saveWidget.data.groupCenter })), (((_52 = saveWidget.data) === null || _52 === void 0 ? void 0 : _52.groupScale) !== undefined && { groupScale: saveWidget.data.groupScale })), (((_53 = saveWidget.data) === null || _53 === void 0 ? void 0 : _53.beforeGroupScale) !== undefined && {
        beforeGroupScale: saveWidget.data.beforeGroupScale,
    }));
    return {
        type: 'deck_pool',
        // Preserve the original authored widgetId (e.g. "deck:90" / "cards:1") so
        // initial-config lookups on subsequent re-merges find the right entry.
        // The collision-safe unique runtime identity lives in `data.id` (deckId),
        // not widgetId — every consumer that needs the unique id keys off data.id.
        widgetId: saveWidget.widgetId || deckId,
        data: deckDataObj,
    };
}
function mergeNumberFramesWidget(_initialWidget, saveWidget) {
    var _a, _b, _c, _d, _e, _f, _h;
    var uuidPart = extractUuidPart(saveWidget.widgetId);
    // data.id must start with "numberFrame_" (singular) so split('_')[0] = "numberFrame"
    // and rightConfigStore["numberFrame"] is found. Server may send colon-format id.
    var rawId = (_a = saveWidget.data) === null || _a === void 0 ? void 0 : _a.id;
    var normalizedId = (rawId === null || rawId === void 0 ? void 0 : rawId.startsWith('numberFrame_')) ? rawId : "numberFrame_".concat(uuidPart);
    var rows = (_c = (_b = saveWidget.data) === null || _b === void 0 ? void 0 : _b.rows) !== null && _c !== void 0 ? _c : 2;
    var cols = (_e = (_d = saveWidget.data) === null || _d === void 0 ? void 0 : _d.cols) !== null && _e !== void 0 ? _e : 5;
    var CELL_SIZE = 30;
    return {
        type: 'numberFrames',
        widgetId: saveWidget.widgetId,
        data: __assign(__assign({ frameType: '10', rows: rows, cols: cols, center: { x: 400, y: 400 }, dimensions: (_h = (_f = saveWidget.data) === null || _f === void 0 ? void 0 : _f.dimensions) !== null && _h !== void 0 ? _h : {
                width: cols * CELL_SIZE,
                height: rows * CELL_SIZE,
            }, scale: 1, rotation: 0, show5x5Grid: false, lock: { positioning: false, editing: false }, tokens: {} }, saveWidget.data), { id: normalizedId, type: 'numberFrames' }),
    };
}
function mergeNumberRacksWidget(_initialWidget, saveWidget) {
    var _a;
    var uuidPart = extractUuidPart(saveWidget.widgetId);
    var rawId = (_a = saveWidget.data) === null || _a === void 0 ? void 0 : _a.id;
    var normalizedId = (rawId === null || rawId === void 0 ? void 0 : rawId.startsWith('numberRacks_')) ? rawId : "numberRacks_".concat(uuidPart);
    var defaultRods = Array.from({ length: 10 }, function (_, i) { return makeDefaultRod(i); });
    return {
        type: 'numberRacks',
        widgetId: saveWidget.widgetId,
        data: __assign(__assign({ center: { x: 400, y: 400 }, dimensions: { width: 880, height: 600 }, scale: 1, rotation: 0, lock: { positioning: false, editing: false }, rods: defaultRods }, saveWidget.data), { id: normalizedId, type: 'numberRacks' }),
    };
}
function mergeColoredTilesWidget(_initialWidget, saveWidget) {
    var _a, _b, _c, _d, _e, _f, _h;
    var uuidPart = extractUuidPart(saveWidget.widgetId);
    var rawId = (_a = saveWidget.data) === null || _a === void 0 ? void 0 : _a.id;
    var normalizedId = (rawId === null || rawId === void 0 ? void 0 : rawId.startsWith('coloredTiles_')) ? rawId : "coloredTiles_".concat(uuidPart);
    var rows = (_c = (_b = saveWidget.data) === null || _b === void 0 ? void 0 : _b.rows) !== null && _c !== void 0 ? _c : 2;
    var columns = (_e = (_d = saveWidget.data) === null || _d === void 0 ? void 0 : _d.columns) !== null && _e !== void 0 ? _e : 2;
    return {
        type: 'coloredTiles',
        widgetId: saveWidget.widgetId,
        data: __assign(__assign({ mode: 'grid', rows: rows, columns: columns, center: { x: 400, y: 400 }, dimensions: (_h = (_f = saveWidget.data) === null || _f === void 0 ? void 0 : _f.dimensions) !== null && _h !== void 0 ? _h : { width: rows * 110, height: columns * 110 }, scale: 1, lock: { positioning: false, editing: false }, isGroup: false }, saveWidget.data), { id: normalizedId, type: 'coloredTiles' }),
    };
}
function mergeLinearPieceWidget(_initialWidget, saveWidget) {
    var _a;
    var uuidPart = extractUuidPart(saveWidget.widgetId);
    var rawId = (_a = saveWidget.data) === null || _a === void 0 ? void 0 : _a.id;
    var normalizedId = (rawId === null || rawId === void 0 ? void 0 : rawId.startsWith('linearPiece_')) ? rawId : "linearPiece_".concat(uuidPart);
    return {
        type: 'linearPiece',
        widgetId: saveWidget.widgetId,
        data: __assign(__assign({ orientation: 'vertical', center: { x: 400, y: 400 }, scale: 1, dimensions: { width: 10, height: 110 }, lock: { positioning: false, editing: false } }, saveWidget.data), { id: normalizedId, type: 'linearPiece' }),
    };
}
function mergeNumberPieceWidget(_initialWidget, saveWidget) {
    var _a;
    var uuidPart = extractUuidPart(saveWidget.widgetId);
    var rawId = (_a = saveWidget.data) === null || _a === void 0 ? void 0 : _a.id;
    var normalizedId = (rawId === null || rawId === void 0 ? void 0 : rawId.startsWith('numberPiece_')) ? rawId : "numberPiece_".concat(uuidPart);
    return {
        type: 'numberPiece',
        widgetId: saveWidget.widgetId,
        data: __assign(__assign({ center: { x: 400, y: 400 }, dimensions: { width: 55, height: 55 }, defaultDimensions: { width: 55, height: 55 }, scale: 1, rotation: 0, lock: { positioning: false, editing: false }, zIndex: 1 }, saveWidget.data), { id: normalizedId, type: 'numberPiece' }),
    };
}
function mergeMoneyPieceWidget(_initialWidget, saveWidget) {
    var _a;
    var uuidPart = extractUuidPart(saveWidget.widgetId);
    var rawId = (_a = saveWidget.data) === null || _a === void 0 ? void 0 : _a.id;
    var normalizedId = (rawId === null || rawId === void 0 ? void 0 : rawId.startsWith('moneyPiece_')) ? rawId : "moneyPiece_".concat(uuidPart);
    return {
        type: 'moneyPiece',
        widgetId: saveWidget.widgetId,
        data: __assign(__assign({ center: { x: 400, y: 400 }, dimensions: { width: 55, height: 55 }, defaultDimensions: { width: 55, height: 55 }, scale: 1, rotation: 0, lock: { positioning: false, editing: false }, zIndex: 1 }, saveWidget.data), { id: normalizedId, type: 'moneyPiece' }),
    };
}
function mergeNumberChartsWidget(_initialWidget, saveWidget) {
    var _a;
    var uuidPart = extractUuidPart(saveWidget.widgetId);
    // Accept saved id only when it already has the correct underscore prefix.
    // Server payloads may send data.id in colon format ("numberChart:uuid")
    // which breaks split('_')[0] right-panel lookup — regenerate from widgetId instead.
    var rawId = (_a = saveWidget.data) === null || _a === void 0 ? void 0 : _a.id;
    var normalizedId = (rawId === null || rawId === void 0 ? void 0 : rawId.startsWith('numberChart_')) ? rawId : "numberChart_".concat(uuidPart);
    return {
        type: 'numberCharts',
        widgetId: saveWidget.widgetId,
        data: __assign(__assign(__assign(__assign({}, DEFAULT_CHART_DATA), { center: { x: 400, y: 400 }, scale: 1, lock: { positioning: false, editing: false } }), saveWidget.data), { id: normalizedId, type: 'numberCharts' }),
    };
}
function mergeAnalogWidget(_initialWidget, saveWidget) {
    var _a, _b;
    // widgetId uses "clock:<uuid>" format; data.id must start with "analog_clock_"
    // so getManipulativeType returns 'analog' and right panel resolves correctly.
    var uuidPart = saveWidget.widgetId.includes(':')
        ? saveWidget.widgetId.split(':')[1]
        : saveWidget.widgetId;
    var normalizedId = (_b = (_a = saveWidget.data) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : "analog_clock_".concat(uuidPart);
    return {
        type: 'analog',
        widgetId: saveWidget.widgetId,
        data: __assign(__assign({ analogType: 'arabic', movement: 'geared', hourhandVisible: true, minutehandVisible: true, handLabelVisible: false, hour: 0, minute: 0, period: 'AM', hourAngle: 0, minuteAngle: 0, timerSetterVisible: false, timer_minutes: 0, timer_seconds: 0, updateStyleOnwhiteboard: true }, saveWidget.data), { id: normalizedId, type: 'analog' }),
    };
}
function mergeClockWidget(_initialWidget, saveWidget) {
    var _a, _b, _c, _d, _e;
    // Timer clock — normalize data.id to "timer_clock_" prefix so
    // getManipulativeType returns 'timer' and right panel opens correctly.
    var uuidPart = saveWidget.widgetId.includes(':')
        ? saveWidget.widgetId.split(':')[1]
        : saveWidget.widgetId;
    var normalizedId = (_b = (_a = saveWidget.data) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : "timer_clock_".concat(uuidPart);
    // data.type is stripped by manipulativeToSaveWidget so saveWidget.data.type
    // is always undefined on reload. Detect timer from payload fields:
    // timer payloads carry timer_minutes/timer_seconds; analog-only payloads don't.
    // WhiteBoard lasso selection (line 1020) and bounding-box checks both key on
    // _man.data.type === 'timer', so this must be set correctly.
    var isTimer = ((_c = saveWidget.data) === null || _c === void 0 ? void 0 : _c.type) === 'timer' ||
        ((_d = saveWidget.data) === null || _d === void 0 ? void 0 : _d.timer_minutes) !== undefined ||
        ((_e = saveWidget.data) === null || _e === void 0 ? void 0 : _e.timerSetterVisible) !== undefined;
    return {
        type: 'clock',
        widgetId: saveWidget.widgetId,
        data: __assign(__assign({}, saveWidget.data), { id: normalizedId, type: isTimer ? 'timer' : 'clock' }),
    };
}
function mergeArrayModelWidget(_initialWidget, saveWidget) {
    var _a, _b, _c, _d, _e, _f, _h, _j, _k;
    var uuidPart = extractUuidPart(saveWidget.widgetId);
    var rawId = (_a = saveWidget.data) === null || _a === void 0 ? void 0 : _a.id;
    var normalizedId = (rawId === null || rawId === void 0 ? void 0 : rawId.startsWith('arrayModel_')) ? rawId : "arrayModel_".concat(uuidPart);
    var DEFAULT_CELL = 55;
    var rows = (_c = (_b = saveWidget.data) === null || _b === void 0 ? void 0 : _b.rows) !== null && _c !== void 0 ? _c : 4;
    var columns = (_e = (_d = saveWidget.data) === null || _d === void 0 ? void 0 : _d.columns) !== null && _e !== void 0 ? _e : 4;
    var cellSize = (_h = (_f = saveWidget.data) === null || _f === void 0 ? void 0 : _f.cellSize) !== null && _h !== void 0 ? _h : DEFAULT_CELL;
    return {
        type: 'arrayModel',
        widgetId: saveWidget.widgetId,
        data: __assign(__assign({ mode: 'grid', rows: rows, columns: columns, cellSize: cellSize }, saveWidget.data), { id: normalizedId, type: 'arrayModels', dimensions: (_k = (_j = saveWidget.data) === null || _j === void 0 ? void 0 : _j.dimensions) !== null && _k !== void 0 ? _k : {
                width: columns * cellSize,
                height: rows * cellSize,
            } }),
    };
}
function mergeFractionTilesWidget(_initialWidget, saveWidget) {
    var _a;
    var uuidPart = extractUuidPart(saveWidget.widgetId);
    // Accept saved id only when it already has the correct underscore prefix.
    // Server payloads may send data.id in colon format ("fractionTiles:uuid")
    // which breaks split('_')[0] right-panel lookup — regenerate from widgetId instead.
    var rawId = (_a = saveWidget.data) === null || _a === void 0 ? void 0 : _a.id;
    var normalizedId = (rawId === null || rawId === void 0 ? void 0 : rawId.startsWith('fractionTiles_')) ? rawId : "fractionTiles_".concat(uuidPart);
    return {
        type: 'fractionTiles',
        widgetId: saveWidget.widgetId,
        data: __assign(__assign({ center: { x: 400, y: 400 }, dimensions: { width: 660, height: 55 }, scale: 1, rotation: 0, lock: { positioning: false, editing: false }, shapeType: 'rectangle', lablesType: 'labeled', customLabel: null, fractionSize: 'fraction-1' }, saveWidget.data), { id: normalizedId, type: 'fractionTiles' }),
    };
}
/**
 * Merges a patternShapes widget: normalises the id prefix and supplies
 * safe defaults for all fields the component reads without fallbacks.
 */
function mergePatternShapesWidget(initialWidget, saveWidget) {
    var _a, _b;
    // widgetId format is "patternShapes:<uuid>" (colon, no dashes).
    // data.id must start with "patternShapes_" so split('_')[0] = "patternShapes"
    // and right-panel config key / getManipulativeType lookups resolve correctly.
    var uuidPart = extractUuidPart(saveWidget.widgetId);
    var rawPsId = (_a = saveWidget.data) === null || _a === void 0 ? void 0 : _a.id;
    var normalizedId = (rawPsId === null || rawPsId === void 0 ? void 0 : rawPsId.startsWith('patternShapes_'))
        ? rawPsId
        : "patternShapes_".concat(uuidPart);
    return {
        type: 'patternShapes',
        widgetId: saveWidget.widgetId,
        data: __assign(__assign(__assign({ 
            // PatternShapesManipulative and the selection group destructure these
            // fields directly with NO internal fallbacks — supply safe defaults so
            // the component never crashes on a minimal IDM payload.
            center: { x: 400, y: 400 }, rotation: 0, scale: 1, color: 'BLUE', lock: { positioning: false, editing: false }, shapeType: 'hexagon' }, ((_b = initialWidget === null || initialWidget === void 0 ? void 0 : initialWidget.data) !== null && _b !== void 0 ? _b : {})), saveWidget.data), { id: normalizedId, type: 'patternShapes' }),
    };
}
/**
 * Merges spinner widget: sections from initial (if any), position from save
 */
function mergeSpinnerWidget(initialWidget, saveWidget) {
    var _a, _b, _c, _d, _e, _f, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5;
    // The runtime id must start with `spinner_` so selection lookups
    // (`tool.split('_')[0] === 'spinner'`) resolve the right-panel config.
    // IDM save state's widgetId is typically `spinner:xxx` (colon), which
    // would otherwise break those lookups. Generate a fresh underscore-
    // prefixed runtime id; the outer `widgetId` still carries the stable
    // persisted key for save round-trip.
    var runtimeId = "spinner_".concat(uuidv4().replace(/-/g, ''));
    var spinnerDataObj = __assign(__assign(__assign(__assign(__assign({ id: runtimeId, type: 'spinner', center: (_b = (_a = saveWidget.data) === null || _a === void 0 ? void 0 : _a.center) !== null && _b !== void 0 ? _b : { x: 400, y: 400 }, radius: (_d = (_c = saveWidget.data) === null || _c === void 0 ? void 0 : _c.radius) !== null && _d !== void 0 ? _d : 200, scale: (_f = (_e = saveWidget.data) === null || _e === void 0 ? void 0 : _e.scale) !== null && _f !== void 0 ? _f : 1, rotation: (_j = (_h = saveWidget.data) === null || _h === void 0 ? void 0 : _h.rotation) !== null && _j !== void 0 ? _j : 0, 
        // Prefer the saved sections (user may have edited them); fall back to the
        // initial config only when the save state didn't capture them.
        sections: (_l = (_k = saveWidget.data) === null || _k === void 0 ? void 0 : _k.sections) !== null && _l !== void 0 ? _l : (_m = initialWidget === null || initialWidget === void 0 ? void 0 : initialWidget.data) === null || _m === void 0 ? void 0 : _m.sections, sectionStartAngle: (_r = (_p = (_o = saveWidget.data) === null || _o === void 0 ? void 0 : _o.sectionStartAngle) !== null && _p !== void 0 ? _p : (_q = initialWidget === null || initialWidget === void 0 ? void 0 : initialWidget.data) === null || _q === void 0 ? void 0 : _q.sectionStartAngle) !== null && _r !== void 0 ? _r : -90, arrowOnly: (_v = (_t = (_s = saveWidget.data) === null || _s === void 0 ? void 0 : _s.arrowOnly) !== null && _t !== void 0 ? _t : (_u = initialWidget === null || initialWidget === void 0 ? void 0 : initialWidget.data) === null || _u === void 0 ? void 0 : _u.arrowOnly) !== null && _v !== void 0 ? _v : false, 
        // Default spinners to locked when the source JSON has no lock set (IDM
        // authoring path). User-saved spinners carry their own `lock` so reload
        // preserves whatever state the user chose.
        lock: (_x = (_w = saveWidget.data) === null || _w === void 0 ? void 0 : _w.lock) !== null && _x !== void 0 ? _x : { positioning: true, editing: true }, 
        // Use presence of saved `lock` as the user-created signal (same logic as dice/deck merge).
        isPreset: (_z = (_y = saveWidget.data) === null || _y === void 0 ? void 0 : _y.isPreset) !== null && _z !== void 0 ? _z : ((_0 = saveWidget.data) === null || _0 === void 0 ? void 0 : _0.lock) === undefined }, (((_1 = saveWidget.data) === null || _1 === void 0 ? void 0 : _1.defaultDimensions) && {
        defaultDimensions: saveWidget.data.defaultDimensions,
    })), (((_2 = saveWidget.data) === null || _2 === void 0 ? void 0 : _2.groupId) && { groupId: saveWidget.data.groupId })), (((_3 = saveWidget.data) === null || _3 === void 0 ? void 0 : _3.groupCenter) && { groupCenter: saveWidget.data.groupCenter })), (((_4 = saveWidget.data) === null || _4 === void 0 ? void 0 : _4.groupScale) !== undefined && { groupScale: saveWidget.data.groupScale })), (((_5 = saveWidget.data) === null || _5 === void 0 ? void 0 : _5.beforeGroupScale) !== undefined && {
        beforeGroupScale: saveWidget.data.beforeGroupScale,
    }));
    return {
        type: 'spinner',
        widgetId: saveWidget.widgetId,
        data: spinnerDataObj,
    };
}
/**
 * Converts a save-state entity (counter on canvas) to a manipulativeInterface
 */
export function entityToManipulative(entity, trayCounters) {
    var _a, _b, _c, _d, _e, _f, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x;
    var trayCounter = trayCounters.find(function (tc) { return tc.counterId === entity.counterId; });
    if (((_a = trayCounter === null || trayCounter === void 0 ? void 0 : trayCounter.hackObj) === null || _a === void 0 ? void 0 : _a.widgetId) === 'arrayModel') {
        var id = "arrayModel_".concat(uuidv4().replace(/-/g, ''));
        var DEFAULT_CELL = 55; // matches ArrayModel.DEFAULT_WORKSPACE_CELL_SIZE
        var props = (_b = trayCounter.hackObj.properties) !== null && _b !== void 0 ? _b : {};
        var rows = (_c = props.rows) !== null && _c !== void 0 ? _c : 4;
        var columns = (_d = props.columns) !== null && _d !== void 0 ? _d : 4;
        var cellSize = (_e = props.cellSize) !== null && _e !== void 0 ? _e : DEFAULT_CELL;
        var arrayData = __assign(__assign({ id: id, type: 'arrayModels', mode: 'grid', rows: rows, columns: columns, cellSize: cellSize, scale: 1, center: { x: entity.data.position.x, y: entity.data.position.y }, lock: { positioning: false, editing: false } }, props), { dimensions: { width: columns * cellSize, height: rows * cellSize } });
        return { type: 'arrayModel', widgetId: id, data: arrayData };
    }
    if (((_f = trayCounter === null || trayCounter === void 0 ? void 0 : trayCounter.hackObj) === null || _f === void 0 ? void 0 : _f.widgetId) === 'linkingCubes') {
        var id = "linkingCube_".concat(uuidv4().replace(/-/g, ''));
        // Historical cube size when legacy `cubeSize` props were authored (do not change —
        // changing this would silently rescale all old saved widgets on load).
        var DEFAULT_CUBE_SIZE = 50;
        var props = __assign({}, ((_h = trayCounter.hackObj.properties) !== null && _h !== void 0 ? _h : {}));
        // Convenience: `cubeSize` in properties maps to scale so authors can think in pixels.
        if (props.cubeSize !== undefined && props.scale === undefined) {
            props.scale = props.cubeSize / DEFAULT_CUBE_SIZE;
        }
        delete props.cubeSize;
        var linkingData = __assign(__assign({ id: id, type: 'linkingCubes', center: { x: entity.data.position.x, y: entity.data.position.y }, orientation: (_j = props.orientation) !== null && _j !== void 0 ? _j : 'horizontal', scale: (_k = props.scale) !== null && _k !== void 0 ? _k : 1, rotation: (_m = (_l = entity.data.rotation) !== null && _l !== void 0 ? _l : entity.data.angle) !== null && _m !== void 0 ? _m : 0, lock: { positioning: false, editing: false } }, props), { color: resolveLinkingCubeColor(props.color) });
        return { type: 'linkingCubes', widgetId: id, data: linkingData };
    }
    // Tray-backed IDM counters render their own artwork. We keep a generic shape
    // as fallback in case the tray entry has no image, but the image takes
    // priority at render time (see Counter.tsx).
    // For duplicated counters (widgetId = "counter_<uuid>") there is no trayCounter
    // match, so fall back to the image URLs persisted in entity.data.
    var frontImage = (trayCounter === null || trayCounter === void 0 ? void 0 : trayCounter.frontImage) || (trayCounter === null || trayCounter === void 0 ? void 0 : trayCounter.primaryImage) || entity.data.customImage;
    // A tray counter is "card-like" only when the backend ships an explicit
    // `backImage` distinct from the front. The legacy `rotate: true` heuristic
    // would otherwise treat plain rotatable tiles (front == primary) as cards
    // and surface a flip button that does nothing useful.
    var backImage = (trayCounter === null || trayCounter === void 0 ? void 0 : trayCounter.backImage) && trayCounter.backImage !== frontImage
        ? trayCounter.backImage
        : ((_o = entity.data.customImageBack) !== null && _o !== void 0 ? _o : undefined);
    var rawShow = entity.data.showImage;
    var showImage = rawShow === 'back' ? 'back' : 'front';
    var dims = resolveCounterDimensions(frontImage, entity.counterId);
    console.log('[counter-size] entityToManipulative', {
        counterId: entity.counterId,
        frontImage: frontImage,
        resolvedDims: dims,
        savedDimensions: entity.data.dimensions,
        finalDimensions: (_p = entity.data.dimensions) !== null && _p !== void 0 ? _p : dims,
        hasTrayMatch: !!trayCounter,
    });
    // Tray counters (type 'counter', with customImage) use the `counter_` prefix.
    // NumberFrame tokens (type 'numberFrameCounter') use `numberFrameCounter_`.
    // Selection-driven lookups (TOOL_RULES in GameController, `tool.split('_')[0]`,
    // etc.) resolve the toolbox config from this prefix. IDM's counterId can be
    // anything (e.g. "apple") and is preserved separately as `counterId`.
    var counterDataObj = __assign(__assign(__assign(__assign(__assign(__assign({ id: "counter_".concat(uuidv4().replace(/-/g, '')), counterId: entity.counterId, type: 'counter', shape: (_q = entity.data.shape) !== null && _q !== void 0 ? _q : 'circle', color: (_r = entity.data.color) !== null && _r !== void 0 ? _r : 'RED', center: {
            x: entity.data.position.x,
            y: entity.data.position.y,
        }, 
        // Prefer the saved dimensions (user may have resized) over the recomputed
        // image-natural dims. Fall back to the image-natural dims for IDM payloads
        // that don't carry dimensions.
        dimensions: (_s = entity.data.dimensions) !== null && _s !== void 0 ? _s : dims, defaultDimensions: (_t = entity.data.defaultDimensions) !== null && _t !== void 0 ? _t : dims, scale: (_u = entity.data.scale) !== null && _u !== void 0 ? _u : 1, rotation: (_w = (_v = entity.data.rotation) !== null && _v !== void 0 ? _v : entity.data.angle) !== null && _w !== void 0 ? _w : 0, lock: (_x = entity.data.lock) !== null && _x !== void 0 ? _x : { positioning: false, editing: false }, customImage: frontImage, customImageBack: backImage }, (entity.data.groupId && { groupId: entity.data.groupId })), (entity.data.groupPosition !== undefined && { groupPosition: entity.data.groupPosition })), (entity.data.groupCenter && { groupCenter: entity.data.groupCenter })), (entity.data.groupScale !== undefined && { groupScale: entity.data.groupScale })), (entity.data.beforeGroupScale !== undefined && {
        beforeGroupScale: entity.data.beforeGroupScale,
    })), (backImage ? { showImage: showImage } : {}));
    return {
        type: 'counter',
        widgetId: entity.counterId,
        data: counterDataObj,
    };
}
/**
 * Converts a manipulativeInterface back to save-state widget format.
 *
 * The merge functions (e.g. `mergeSpinnerWidget`) only accept fields that are
 * present in the saved payload — anything missing falls back to a default and
 * the widget visibly resets on reload. So this serialiser must preserve every
 * field needed to reconstruct the on-screen state, not just position/size.
 */
export function manipulativeToSaveWidget(m) {
    var _a, _b;
    var data = m.data;
    // Spread all runtime data, stripping fields that are either reconstituted
    // by the merge fallback (id from widgetId, type from the save-type map) or
    // are purely ephemeral and must not be persisted.
    var _id = data.id, _type = data.type, 
    // numberPiece / moneyPiece animation flags — transient, reset on mount
    _animation = data.animation, _animating = data.animating, _animatingOut = data.animatingOut, 
    // timer running state — always starts paused after a reload
    _timerRunning = data.timer_is_running, saveData = __rest(data, ["id", "type", "animation", "animating", "animatingOut", "timer_is_running"]);
    // numberFrames tokens reference the frame via frameId = frame's data.id.
    // Stripping id breaks the link on reload — preserve it so future saves round-trip correctly.
    if (m.type === 'numberFrames' && _id) {
        saveData.id = _id;
    }
    // deck_pool: two decks from the same IDM picker (widgetId "cards:1") both
    // produce extractUuidPart → "1", colliding at "deck_1" on reload. Persist
    // the runtime id so mergeDeckWidget can recover the correct unique deckId
    // without relying solely on the cards' embedded deckId fields.
    if (m.type === 'deck_pool' && _id) {
        saveData.id = _id;
    }
    // numberLine / jumpBar / customTick: jumpBars and customTicks reference
    // their parent line via numberLineId = the line's data.id. The line's
    // `widgetId` is a separate, independently-generated identifier (different
    // format and uuid) — falling back to it on reload orphans every attached
    // jumpBar/customTick. Preserve the real id so the link survives reload.
    if ((m.type === 'numberLine' || m.type === 'jumpBar' || m.type === 'customTick') &&
        _id) {
        saveData.id = _id;
    }
    // coinsAndCounters: `widgetId` is a separately-generated identifier
    // (colon-delimited, different uuid) from the runtime `data.id`
    // (underscore-delimited). RightSideToolPanel derives its config-panel
    // lookup key via `tool.split('_')[0]` — falling back to the colon-form
    // widgetId on reload breaks that split, so the right panel silently
    // fails to open after a save/reload round trip. Preserve the real id.
    if (m.type === 'coinsAndCounters' && _id) {
        saveData.id = _id;
    }
    // Clock (timer) and analog data.type ('timer' | 'analog') distinguishes the two clock
    // variants at runtime. Preserve it so the merge function can restore it on reload
    // instead of having to infer it from payload fields.
    if ((m.type === 'clock' || m.type === 'analog') && _type) {
        saveData.type = _type;
    }
    return {
        widgetId: (_b = (_a = m.widgetId) !== null && _a !== void 0 ? _a : data.id) !== null && _b !== void 0 ? _b : '',
        type: reverseMapType(m.type),
        data: saveData,
    };
}
/**
 * Converts a counter manipulativeInterface back to entity format.
 *
 * Counters can be resized (scale/dimensions), locked, grouped, and recoloured
 * after they're dropped on the canvas. The `entityToManipulative` reader now
 * pulls these fields back, so we must serialise every piece of runtime state
 * that affects how the counter looks — not just position + face.
 */
export function manipulativeToEntity(m) {
    var _a, _b, _c;
    var data = m.data;
    var rot = (_a = data.rotation) !== null && _a !== void 0 ? _a : 0;
    return {
        counterId: (_b = m.widgetId) !== null && _b !== void 0 ? _b : data.id,
        type: 'counter',
        data: __assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign({ position: { x: data.center.x, y: data.center.y }, 
            // Emit both `angle` (legacy) and `rotation` (IDM) so either consumer
            // round-trips the counter orientation cleanly.
            angle: rot, rotation: rot, 
            // Card-like counters persist their current face ('front' | 'back').
            showImage: (_c = data.showImage) !== null && _c !== void 0 ? _c : 'front' }, (data.scale !== undefined && { scale: data.scale })), (data.dimensions && { dimensions: data.dimensions })), (data.defaultDimensions && { defaultDimensions: data.defaultDimensions })), (data.lock && { lock: data.lock })), (data.shape && { shape: data.shape })), (data.color && { color: data.color })), (data.groupId && { groupId: data.groupId })), (data.groupPosition !== undefined && { groupPosition: data.groupPosition })), (data.groupCenter && { groupCenter: data.groupCenter })), (data.groupScale !== undefined && { groupScale: data.groupScale })), (data.beforeGroupScale !== undefined && { beforeGroupScale: data.beforeGroupScale })), (data.customImage && { customImage: data.customImage })), (data.customImageBack && { customImageBack: data.customImageBack })),
    };
}
/**
 * Reverse maps internal type back to save-state type
 */
function reverseMapType(internalType) {
    switch (internalType) {
        case 'dice':
            return 'roller';
        case 'spinner':
            return 'spinner';
        default:
            return internalType;
    }
}
